import { observer } from "mobx-react"
import { createContext, type MouseEvent as ReactMouseEvent, type ReactNode, useCallback, useContext, useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { DiffsWorkerProvider } from "../components/DiffsWorkerProvider"
import { OfflineIndicator, UpdateBanner, UpdateErrorBanner } from "../components/UpdateBanner"
import { ReleaseNotification } from "../components/notifications/ReleaseNotification"
import { CodeSidebar } from "../components/sidebar/Sidebar"
import { codeSidebarManager } from "../components/sidebar/sidebarManager"
import SidebarIcon from "../components/sidebar/static/sidebar.svg?react"
import { CodeModalProvider } from "../components/ui/CodeModalProvider"
import { onUpdateAvailable, onUpdateError } from "../electronAPIs/app"
import { hasElectronIpc } from "../electronAPIs/capabilities"
import { openExternalUrl } from "../platform/actions"
import { type FrameColors, windowFrameEnabled, windowFrameSetColors } from "../electronAPIs/windowFrame"
import { PortalContainerProvider } from "../hooks/usePortalContainer"
import { useMediaQuery } from "../hooks/useMediaQuery"
import { useOnlineStatus } from "../hooks/useOnlineStatus"
import { useResolvedTheme } from "../hooks/useResolvedTheme"
import { type PlatformInfo, fetchPlatformInfo } from "../nestAPIs/platform"
import { productModuleRightPaneID } from "../modules/registry"
import { useCodeStore } from "../store/context"
import { dockedPaneWidthStore } from "../store/managers/dockedPaneWidthStore"
import { getExternalUrlToOpen } from "../utils/externalLinks"
import "../tw.css"
import { Z_INDEX } from "../constants"

const ELECTRON_DRAG_REGION_CLASS = "electron-drag-region"
const ELECTRON_NO_DRAG_REGION_CLASS = "electron-no-drag-region"
const ELECTRON_WINDOW_CONTROLS_REGION_CLASS = "electron-window-controls-region"
type ShellPlatform = PlatformInfo["platform"]

interface NavbarProps {
    title: string | ReactNode | null
    icon: ReactNode | null
    center?: ReactNode
    below?: ReactNode
    right: ReactNode | null
    noBorder?: boolean
    overlay?: boolean
    visible?: boolean
}

interface CodeAppLayoutProps {
    children: ReactNode
    navbar: NavbarProps
    frameCenter?: ReactNode
}

const TaskNavbarContext = createContext<((inline: boolean) => ReactNode) | null>(null)

export const TaskNavbarSlot = ({ inline = false }: { inline?: boolean }) => {
    const renderNavbar = useContext(TaskNavbarContext)
    return renderNavbar?.(inline) ?? null
}

const detectRendererPlatform = (): ShellPlatform | null => {
    if (typeof navigator === "undefined") return null
    const platform = navigator.platform.toLowerCase()
    if (platform.includes("mac")) return "darwin"
    if (platform.includes("win")) return "win32"
    if (platform.includes("linux")) return "linux"
    return null
}

const useElectronPlatform = (): ShellPlatform | null => {
    const [platform, setPlatform] = useState<ShellPlatform | null>(() => (hasElectronIpc() ? detectRendererPlatform() : null))

    useEffect(() => {
        if (!hasElectronIpc()) return

        let cancelled = false
        fetchPlatformInfo().then((platformInfo) => {
            if (!cancelled) setPlatform(platformInfo.platform)
        })

        return () => {
            cancelled = true
        }
    }, [])

    return platform
}

const WithNavbar = observer((props: { children: ReactNode; navbar: NavbarProps }) => {
    const { children, navbar } = props
    const sidebarManager = codeSidebarManager
    const compact = useMediaQuery("(max-width: 719px)")
    const moduleRightPaneDocked = productModuleRightPaneID() !== null && !compact
    const localTaskPaneDocked = sidebarManager.taskPaneDocked && !moduleRightPaneDocked
    const paneDocked = localTaskPaneDocked || moduleRightPaneDocked
    const phone = useMediaQuery("(max-width: 720px)")
    const sidebarVisible = !phone && sidebarManager.showSidebar
    const shellPlatform = useElectronPlatform()
    const insetForTrafficLights = shellPlatform === "darwin" && !sidebarVisible
    const insetForWindowControls = shellPlatform === "win32" || shellPlatform === "linux"
    const navbarHidden = navbar.overlay && navbar.visible === false && !paneDocked
    const openSidebarButton = !sidebarVisible ? (
        <button
            type="button"
            className={`${ELECTRON_NO_DRAG_REGION_CLASS} btn flex h-7 w-7 flex-shrink-0 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content max-[720px]:h-9 max-[720px]:w-9`}
            onClick={sidebarManager.toggleSidebar}
            title="Open sidebar"
            aria-label="Open sidebar"
        >
            <SidebarIcon className="w-4 h-4" />
        </button>
    ) : null
    const navbarTitle = (inline: boolean) =>
        navbar.title ? (
            <div className={`flex min-w-0 flex-1 items-center ${inline ? "text-sm" : "text-base"}`}>
                {typeof navbar.title === "string" ? (
                    <div className="min-w-0 truncate font-medium leading-none text-base-content" title={navbar.title}>
                        {navbar.title}
                    </div>
                ) : (
                    navbar.title
                )}
            </div>
        ) : null
    const renderNavbar = (inline = false) => (
        <div
            className={twMerge(
                ELECTRON_DRAG_REGION_CLASS,
                "px-3 bg-base-100 w-full min-w-0 overflow-hidden flex items-center flex-shrink-0",
                inline ? "queue-cockpit-navbar relative" : "h-11",
                navbar.overlay && !inline ? "absolute inset-x-0 top-0 border-b border-border" : "relative",
                navbarHidden && !inline ? "pointer-events-none" : "",
                insetForTrafficLights ? "pl-[90px]" : "",
                insetForWindowControls ? ELECTRON_WINDOW_CONTROLS_REGION_CLASS : ""
            )}
            style={
                navbar.overlay && !inline
                    ? {
                          zIndex: Z_INDEX.APP_NAVBAR,
                          opacity: navbarHidden ? 0 : 1,
                          transform: navbarHidden ? "translate3d(0, -100%, 0)" : "translate3d(0, 0, 0)",
                          transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 240ms ease",
                          willChange: "transform, opacity",
                          width: "auto",
                          right: 0,
                      }
                    : inline && insetForTrafficLights
                      ? { paddingLeft: 90 }
                      : undefined
            }
            data-navbar-visible={navbarHidden && !inline ? "false" : "true"}
        >
            {navbar.center ? (
                <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                    <div className={`${ELECTRON_NO_DRAG_REGION_CLASS} flex min-w-0 items-center gap-1 overflow-hidden`}>
                        {openSidebarButton}
                        {navbarTitle(inline)}
                    </div>
                    <div className={`${ELECTRON_NO_DRAG_REGION_CLASS} z-20`}>{navbar.center}</div>
                    <div className={`${ELECTRON_NO_DRAG_REGION_CLASS} flex min-w-0 items-center justify-end overflow-hidden`}>
                        {navbar.right ? <div className="flex items-center gap-2">{navbar.right}</div> : null}
                    </div>
                </div>
            ) : (
                <div
                    className={
                        inline
                            ? "flex w-full min-w-0 items-center gap-1"
                            : `grid w-full items-center transition-all duration-[500ms] ease-in-out ${
                                  sidebarVisible ? "grid-cols-[0px_1fr_auto]" : "grid-cols-[32px_1fr_auto]"
                              }`
                    }
                >
                    <div className="shrink-0 overflow-hidden">{openSidebarButton}</div>
                    <div className={`${ELECTRON_NO_DRAG_REGION_CLASS} flex min-w-0 flex-1 items-center overflow-hidden`}>{navbarTitle(inline)}</div>
                    {navbar.right && <div className={`${ELECTRON_NO_DRAG_REGION_CLASS} ml-2 flex items-center gap-2`}>{navbar.right}</div>}
                </div>
            )}
        </div>
    )

    return (
        <TaskNavbarContext.Provider value={navbar.overlay ? renderNavbar : null}>
            <div className="flex flex-col h-full w-full bg-base-100" data-shell-platform={shellPlatform ?? "browser"}>
                {navbar.overlay ? null : renderNavbar()}
                {navbar.below ? <div className={`${ELECTRON_NO_DRAG_REGION_CLASS} flex-none`}>{navbar.below}</div> : null}
                <div className="flex flex-col flex-1 w-full bg-base-100 relative min-h-0">{children}</div>
            </div>
        </TaskNavbarContext.Provider>
    )
})

const ElectronFrame = observer((props: { children: ReactNode; resolvedTheme: "light" | "dark"; center?: ReactNode }) => {
    const { children, resolvedTheme, center } = props
    const [frameEnabled, setFrameEnabled] = useState(true)
    const [updateReady, setUpdateReady] = useState(false)
    const [updateError, setUpdateError] = useState(false)
    const online = useOnlineStatus()

    const updateElectronFrameColorsFromTheme = useCallback(async () => {
        const isDark = resolvedTheme === "dark"
        const color: FrameColors = {
            color: isDark ? "#303030" : "#F2F3F5",
            symbolColor: isDark ? "#DBDDE0" : "#555558",
        }
        await windowFrameSetColors(color)
    }, [resolvedTheme])

    useEffect(() => {
        const update = async () => {
            const res = await windowFrameEnabled()
            setFrameEnabled(res)
            await updateElectronFrameColorsFromTheme()
        }
        update()
    }, [updateElectronFrameColorsFromTheme])

    useEffect(() => {
        updateElectronFrameColorsFromTheme()
    }, [updateElectronFrameColorsFromTheme])

    // Subscribe to update available events from Electron
    useEffect(() => {
        const unsubscribe = onUpdateAvailable(() => {
            setUpdateReady(true)
            setUpdateError(false)
        })
        return unsubscribe
    }, [])

    // Subscribe to update error events from Electron
    useEffect(() => {
        const unsubscribe = onUpdateError(() => {
            setUpdateError(true)
        })
        return unsubscribe
    }, [])

    if (!frameEnabled) {
        return <div className="w-full h-full">{children}</div>
    }

    return (
        <div className="w-full h-full relative overflow-hidden bg-base-200">
            {(updateReady || updateError || !online || center) && (
                <div
                    className={`${ELECTRON_NO_DRAG_REGION_CLASS} absolute top-2 left-1/2 flex -translate-x-1/2 items-center gap-1.5`}
                    style={{ zIndex: Z_INDEX.FRAME_STATUS }}
                >
                    {!online && <OfflineIndicator />}
                    {updateReady ? <UpdateBanner /> : updateError ? <UpdateErrorBanner /> : center}
                </div>
            )}
            <div className="flex flex-col w-full h-full overflow-hidden">{children}</div>
        </div>
    )
})

const FramedApp = observer((props: { children: ReactNode; resolvedTheme: "light" | "dark"; center?: ReactNode }) => {
    const { children, resolvedTheme, center } = props

    if (!hasElectronIpc()) {
        return <div className="w-full h-full">{children}</div>
    }
    return (
        <ElectronFrame resolvedTheme={resolvedTheme} center={center}>
            {children}
        </ElectronFrame>
    )
})

export const CodeAppLayout = observer((props: CodeAppLayoutProps) => {
    const { children, navbar, frameCenter } = props
    const codeStore = useCodeStore()
    const sidebarManager = codeSidebarManager
    const compact = useMediaQuery("(max-width: 719px)")
    const moduleRightPaneID = compact ? null : productModuleRightPaneID()
    const themeSetting = codeStore.personalSettingsStore?.settings.current.theme ?? "system"
    const themeClass = useResolvedTheme(themeSetting)
    const isDark = themeClass.includes("dark") || themeClass.includes("black") || themeClass.includes("synthwave") || themeClass.includes("dracula")
    const handleExternalLinkClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        const target = event.target instanceof Element ? event.target : null
        const anchor = target?.closest<HTMLAnchorElement>("a[href]")
        if (!anchor || !event.currentTarget.contains(anchor)) return

        const url = getExternalUrlToOpen(anchor.getAttribute("href"))
        if (!url) return

        event.preventDefault()
        event.stopPropagation()
        void openExternalUrl(url)
    }, [])

    return (
        <div className={`code-theme ${themeClass} w-full h-full overflow-hidden relative flex flex-col`} onClickCapture={handleExternalLinkClick}>
            <FramedApp resolvedTheme={isDark ? "dark" : "light"} center={frameCenter}>
                <DiffsWorkerProvider>
                    <CodeModalProvider>
                        <PortalContainerProvider>
                            <div className="w-full h-full relative overflow-hidden bg-base-200" data-code-app-shell="true">
                                <CodeSidebar />
                                <div
                                    className={twMerge(
                                        "relative flex h-full min-w-0 flex-col overflow-hidden bg-base-100 ease-in-out",
                                        dockedPaneWidthStore.isResizing ? "transition-none" : "transition-[margin-left,margin-right] duration-300",
                                        sidebarManager.showSidebar ? "min-[901px]:ml-[300px] min-[901px]:border-l min-[901px]:border-border" : "ml-0"
                                    )}
                                    style={{ zIndex: Z_INDEX.APP_CONTENT, marginRight: moduleRightPaneID ? dockedPaneWidthStore.cssWidth : 0 }}
                                    data-product-module-right-pane={moduleRightPaneID ?? undefined}
                                >
                                    <div className="h-full w-full relative flex flex-col">
                                        <WithNavbar navbar={navbar}>{children}</WithNavbar>
                                    </div>
                                </div>
                            </div>
                            <ReleaseNotification />
                        </PortalContainerProvider>
                    </CodeModalProvider>
                </DiffsWorkerProvider>
            </FramedApp>
        </div>
    )
})
